import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import { formatCurrency } from '../../../numberUtils';
import './ProjectLogisticsInfo.scss';

const renderEditLink = (
  label,
  section,
  id,
  projectResourceId,
  permissions,
  permission = 'update',
  className = 'required',
  title = null,
) => {
  const data = title ? { placement: 'bottom', toggle: 'tooltip' } : {};
  if (!permissions[permission]) {
    return (
      <h2 className="project-section-title">{label}</h2>
    );
  }

  let url = `/projects/${projectResourceId}/edit?from_project_page=true`;
  if (section) {
    url = [url, `step=${section}`].join('&');
  }

  return (
    <a href={url} id={id} className={className} title={title} data={data}>
      { label }
    </a>
  );
};

const handleStepNavigation = (
  projectResourceId,
  permissions,
  title,
  step = 'start',
) => (
  <Fragment>
    {permissions.update
    && (
    <a
      className="edit-logistics-button"
      data-toggle="tooltip"
      data-placement="bottom"
      title={title}
      href={`/projects/${projectResourceId}/edit?from_project_page=true&step=${step}`}
    >
      <i className="fa fa-edit" />
    </a>
    )}
  </Fragment>
);

const renderBudget = (
  projectResourceId,
  permissions,
  ownerOrCloraRep,
  project,
  hideBaseSalary,
  employerShareView,
  showHourlyRate
) => {
  const {
    budget_maximum: max,
    budget_minimum: min,
    consultant_take_home_maximum: consultantMax,
    consultant_take_home_minimum: consultantMin,
    employment_type: employmentType,
    base_pay_hidden: basePayHidden,
  } = project;
  const budgetText = employmentType === 'full_time' ? 'Annual base salary range: ' : 'Hourly rate: ';
  const editLinkText = employmentType === 'full_time' ? 'Base Salary' : 'Hourly Rate';
  const budget = max && min ? `${formatCurrency(min, 0)} - ${formatCurrency(max, 0)}` : null;
  const consultantBudget = consultantMax && consultantMin ? `${formatCurrency(consultantMin, 0)} - ${formatCurrency(consultantMax, 0)}` : null;
  const hideBudget = (basePayHidden || hideBaseSalary) && employmentType === 'full_time';
  return (
    <Fragment>
      { ownerOrCloraRep && permissions.update
        ? (
          <div className="commitment hourly-rate">
            <i className="fa fa-dollar-sign mr-5" />
            { budget ? `${budgetText} ${budget} ${basePayHidden ? '(Hidden from candidates)' : ''}`
              : renderEditLink(
                editLinkText,
                'logistics',
                null,
                projectResourceId,
                permissions,
                'update',
              )}
            {handleStepNavigation(projectResourceId, permissions, 'Edit Logistics', 'logistics')}
          </div>
        )
        : !hideBudget && budget && showHourlyRate
        && (
        <div className="commitment hourly-rate">
          <i className="fa fa-dollar-sign mr-5" />
          {`${budgetText} ${employerShareView ? budget : consultantBudget}`}
        </div>
        )}
    </Fragment>
  );
};

const renderDurationAndHoursPerWeek = (
  projectResourceId,
  permissions,
  ownerOrCloraRep,
  project,
) => {
  const {
    avg_time_required_range: avgTimeRequiredRange,
    duration_range: durationRange,
  } = project;
  const showEditLink = !avgTimeRequiredRange && !durationRange;
  const hoursPerWeekText = `${avgTimeRequiredRange} hours per week`;
  const durationRangeText = `for ${durationRange} ${durationRange > 1 ? 'months' : 'month'}`;

  return (
    <Fragment>
      {
        ownerOrCloraRep && permissions.update
          ? (
            <div className="commitment duration">
              <i className="fa fa-calendar-day mr-5" />
              {showEditLink
                ? renderEditLink(
                  'Duration and Average Time',
                  'start',
                  null,
                  projectResourceId,
                  permissions,
                  'update',
                )
                : `${hoursPerWeekText} ${durationRangeText}`}
              {handleStepNavigation(projectResourceId, permissions, 'Edit Logistics')}
            </div>
          )
          : !showEditLink
          && (
          <div className="commitment duration">
            <i className="fa fa-calendar-day mr-5" />
            {hoursPerWeekText}
            {' '}
            {durationRangeText}
          </div>
          )
      }
    </Fragment>
  );
};

const renderLocationAndWorktype = (projectResourceId, permissions, ownerOrCloraRep, project) => (
  <Fragment>
    { ownerOrCloraRep && permissions.update
      ? (
        <div className="commitment work-location">
          <i className="fa fa-map-marker-alt mr-5" />
          { project.work_location
            ? `${project.office_and_worktype_information}`
            : renderEditLink(
              'Edit Logistics',
              'start',
              null,
              projectResourceId,
              permissions,
              'update',
            )}
          {handleStepNavigation(projectResourceId, permissions, 'Edit Logistics', 'logistics')}
        </div>
      )
      : project.work_location
      && (
      <div className="commitment work-location">
        <i className="fa fa-map-marker-alt mr-5" />
        {`${project.office_and_worktype_information}`}
      </div>
      )}
  </Fragment>
);

const renderSeniority = (projectResourceId, permissions, ownerOrCloraRep, project) => (
  <Fragment>
    { ownerOrCloraRep && permissions.update
      ? (
        <div className="commitment seniority">
          <i className="fa fa-certificate mr-5" />
          {project.seniority && project.seniority.length
            ? `${project.level_names} Level`
            : renderEditLink(
              'Experience Levels',
              'start',
              null,
              projectResourceId,
              permissions,
              'update',
            )}
          {handleStepNavigation(projectResourceId, permissions, 'Edit Seniority')}
        </div>
      )
      : (project.seniority && project.seniority.length
        ? (
          <div className="commitment seniority">
            <i className="fa fa-certificate mr-5" />
            {`${project.level_names} Level`}
          </div>
        ) : '')}
  </Fragment>
);

const renderTravelRequirement = (projectResourceId, permissions, ownerOrCloraRep, project) => (
  <Fragment>
    { ownerOrCloraRep && permissions.update
      ? (
        <div className="commitment travel">
          <i className="fa fa-plane" />
          {`Travel Requirements: ${project.travel_location_names ? project.travel_location_names : 'N/A'}`}
          {handleStepNavigation(projectResourceId, permissions, 'Edit Logistics', 'logistics')}
        </div>
      )
      : project.travel_location_names
        && (
        <div className="commitment travel">
          <i className="fa fa-plane" />
          {`Travel Requirements: ${project.travel_location_names}`}
        </div>
        )}
  </Fragment>
);

const renderCompensationAndBenefits = (projectResourceId, permissions, ownerOrCloraRep, project) => (
  <Fragment>
    { ownerOrCloraRep && permissions.update
      ? (
        <div className="commitment travel">
          <i className="fa fa-info-circle" />
          {`Compensation & Benefits: ${project.compensation_benefit_note ? project.compensation_benefit_note : 'N/A'}`}
          {handleStepNavigation(projectResourceId, permissions, 'Edit Logistics', 'logistics')}
        </div>
      )
      : project.compensation_benefit_note
      && (
      <div className="commitment travel">
        <i className="fa fa-info-cirle" />
        {`Travel Requirements: ${project.compensation_benefit_note}`}
      </div>
      )}
  </Fragment>
);

function ProjectLogisticsInfo({
  projectResourceId,
  permissions,
  ownerOrCloraRep,
  project,
  hideBaseSalary,
  employerShareView,
  isCollaborator,
  showHourlyRate
}) {
  return (
    <div className="project-logistics-info">
      {renderSeniority(projectResourceId, permissions, ownerOrCloraRep, project)}
      {project.employment_type === 'consulting'
        && renderDurationAndHoursPerWeek(projectResourceId, permissions, ownerOrCloraRep, project)}
      {renderLocationAndWorktype(projectResourceId, permissions, ownerOrCloraRep, project)}
      {renderTravelRequirement(projectResourceId, permissions, ownerOrCloraRep, project)}
      {!isCollaborator && renderBudget(
        projectResourceId,
        permissions,
        ownerOrCloraRep,
        project,
        hideBaseSalary,
        employerShareView,
        showHourlyRate
      )}
      {project.employment_type === 'full_time'
      && renderCompensationAndBenefits(projectResourceId, permissions, ownerOrCloraRep, project)}
    </div>
  );
}

ProjectLogisticsInfo.propTypes = {
  project: PropTypes.shape({}).isRequired,
  projectResourceId: PropTypes.string.isRequired,
  permissions: PropTypes.shape({}),
  ownerOrCloraRep: PropTypes.bool,
  hideBaseSalary: PropTypes.bool,
  employerShareView: PropTypes.bool,
};

ProjectLogisticsInfo.defaultProps = {
  hideBaseSalary: false,
  permissions: {},
  ownerOrCloraRep: false,
  employerShareView: false,
};

export default ProjectLogisticsInfo;
